import {
  Fragment,
  useMemo,
  type ReactNode,
} from "react";
import { Anchor, Box, Stack, Text, Title } from "@mantine/core";
import {
  parseEducationRichText,
  type EducationRichTextMark,
  type EducationRichTextNode,
} from "@evolys/types";
import { isStagingUrl } from "./publicLinks";

function safeLink(value: unknown): string | null {
  if (typeof value !== "string") return null;
  if (value.startsWith("/") && !value.startsWith("//")) return value;

  try {
    const url = new URL(value);
    return url.protocol === "https:" ? url.href : null;
  } catch {
    return null;
  }
}

function renderMarkedText(
  text: string,
  marks: EducationRichTextMark[],
  key: string,
): ReactNode {
  let output: ReactNode = text;

  marks.forEach((mark, index) => {
    const markKey = `${key}-mark-${index}`;
    if (mark.type === "bold") {
      output = <strong key={markKey}>{output}</strong>;
    } else if (mark.type === "italic") {
      output = <em key={markKey}>{output}</em>;
    } else if (mark.type === "underline") {
      output = <u key={markKey}>{output}</u>;
    } else if (mark.type === "strike") {
      output = <s key={markKey}>{output}</s>;
    } else if (mark.type === "code") {
      output = <code key={markKey}>{output}</code>;
    } else if (mark.type === "link") {
      const href = safeLink(mark.attrs?.href);
      if (href && !isStagingUrl(href)) {
        const external = href.startsWith("https://");
        output = (
          <Anchor
            key={markKey}
            href={href}
            target={external ? "_blank" : undefined}
            rel={external ? "noopener noreferrer" : undefined}
          >
            {output}
          </Anchor>
        );
      }
    }
  });

  return output;
}

function renderChildren(
  node: EducationRichTextNode,
  key: string,
): ReactNode[] {
  return (node.content ?? []).map((child, index) =>
    renderNode(child, `${key}-${index}`),
  );
}

function renderNode(node: EducationRichTextNode, key: string): ReactNode {
  if (node.type === "text") {
    return (
      <Fragment key={key}>
        {renderMarkedText(node.text ?? "", node.marks ?? [], key)}
      </Fragment>
    );
  }

  const children = renderChildren(node, key);
  if (node.type === "doc") return <Fragment key={key}>{children}</Fragment>;
  if (node.type === "paragraph") {
    return <Text component="p" key={key}>{children}</Text>;
  }
  if (node.type === "heading") {
    const requested = Number(node.attrs?.level);
    const order = requested === 3 || requested === 4 ? requested : 2;
    return <Title order={order} key={key}>{children}</Title>;
  }
  if (node.type === "bulletList") {
    return <Box component="ul" key={key}>{children}</Box>;
  }
  if (node.type === "orderedList") {
    const start = Number(node.attrs?.start);
    return (
      <Box
        component="ol"
        key={key}
        start={Number.isInteger(start) && start > 0 ? start : undefined}
      >
        {children}
      </Box>
    );
  }
  if (node.type === "listItem") return <li key={key}>{children}</li>;
  if (node.type === "blockquote") {
    return <Box component="blockquote" key={key}>{children}</Box>;
  }
  if (node.type === "horizontalRule") return <hr key={key} />;
  if (node.type === "hardBreak") return <br key={key} />;
  return null;
}

function LegacyArticleBody({ value }: { value: string }) {
  const blocks = useMemo(() => value.split(/\n{2,}/), [value]);
  return (
    <Stack gap="lg">
      {blocks.map((raw, index) => {
        const block = raw.trim();
        if (!block) return null;
        if (block.startsWith("## "))
          return <Title order={2} key={index}>{block.slice(3)}</Title>;
        if (block.startsWith("### "))
          return <Title order={3} key={index}>{block.slice(4)}</Title>;
        if (block.split("\n").every((line) => line.startsWith("- "))) {
          return (
            <Box component="ul" key={index}>
              {block.split("\n").map((line) => (
                <li key={line}><Text component="span">{line.slice(2)}</Text></li>
              ))}
            </Box>
          );
        }
        return <Text component="p" key={index}>{block.replace(/\n/g, " ")}</Text>;
      })}
    </Stack>
  );
}

export function RichArticleBody({ value }: { value: string }) {
  const rich = useMemo(() => parseEducationRichText(value), [value]);
  return (
    <Box className="article-copy">
      {rich
        ? renderNode(rich.document, "article")
        : <LegacyArticleBody value={value} />}
    </Box>
  );
}
